/**
 * A phone number with its country in front, stored the one way WhatsApp
 * accepts it.
 *
 * A number typed as "82205 23246" is not a number anybody can be reached on:
 * Chatlio will take it, hand it to Meta, and Meta will drop it without a
 * word. Every real number is a country code plus a subscriber number, so the
 * form asks for both and stores them joined - digits only, country first, no
 * plus, no leading zero.
 *
 * The country is the field's own state, not read back off the stored value:
 * a stored "" or "918" cannot say which country was picked, and deriving it
 * on every keystroke snapped the select back to the default the moment it
 * was changed. The value only sets the country on the way in - editing a
 * saved contact shows the right country without anybody re-picking it - and
 * whenever a whole number, plus and all, is pasted into the number box.
 *
 * The number box takes digits and spaces only, and says underneath when the
 * count is wrong for the country chosen.
 */
import { useEffect, useState } from 'react';
import { COUNTRIES, splitPhone, joinPhone, flagOf, digitsOnly, countryOf, phoneProblem } from '../lib/phone.js';

export { COUNTRIES, splitPhone, joinPhone };

export default function PhoneField({ value, onChange, autoFocus }) {
  const [code, setCode] = useState(() => splitPhone(value).code);
  const [touched, setTouched] = useState(false);

  const digits = digitsOnly(value);
  const local = digits.startsWith(code) ? digits.slice(code.length) : splitPhone(value).local;

  /* A value arriving from outside with a different country - opening another
     contact to edit - moves the select to match. Typing never lands here,
     because what is typed is always joined onto the current code. */
  useEffect(() => {
    if (digits && !digits.startsWith(code)) {
      const hit = countryOf(digits);
      if (hit) setCode(hit[0]);
    }
  }, [value]);

  const pickCountry = next => {
    setCode(next);
    onChange(joinPhone(next, local));
  };

  const typeNumber = raw => {
    /* "+971 50 695 3496" pasted whole: split it, and move the country too. */
    if (/^\s*\+/.test(raw)) {
      const hit = countryOf(digitsOnly(raw));
      if (hit) {
        setCode(hit[0]);
        onChange(digitsOnly(raw));
        return;
      }
    }
    onChange(joinPhone(code, raw.replace(/[^\d\s]/g, '')));
  };

  const problem = touched && local ? phoneProblem(code, local) : null;

  return (
    <div className={`phonef${problem ? ' bad' : ''}`}>
      <select value={code} onChange={e => pickCountry(e.target.value)} aria-label="Country">
        {COUNTRIES.map(([c, iso, name]) => (
          <option key={c} value={c}>{flagOf(iso)} +{c} {name}</option>
        ))}
      </select>
      <input
        value={local}
        inputMode="tel"
        autoFocus={autoFocus}
        placeholder={placeholderFor(code)}
        maxLength={16}
        onChange={e => typeNumber(e.target.value)}
        onBlur={() => setTouched(true)}
        aria-invalid={problem ? 'true' : undefined}
      />
      {problem ? <small className="phonef-why">{problem}</small> : null}
    </div>
  );
}

/* An example of the right length for the country, so the box shows what it wants. */
function placeholderFor(code) {
  const hit = COUNTRIES.find(([c]) => c === code);
  const n = hit ? hit[3] : 9;
  const sample = '5069534961234'.slice(0, n);
  return sample.replace(/(\d{3})(?=\d)/g, '$1 ');
}
